import { timeStringToMs } from './utility/DateFormatter';
import { Log } from '@lightningjs/sdk';
import { IProgramV5, IScheduleV5, ISeasonV5, ISeriesV5 } from '../api/models/AndromeTV.types';
import { Backend } from '../api/Backend';
import { EpgApiV5 } from '../api/EpgApiV5';

export interface ScheduleTimes {
  from: Date;
  until: Date;
}

export interface ScheduleData {
  schedule: IScheduleV5;
  program: IProgramV5;
  season?: ISeasonV5;
  series?: ISeriesV5;
}

export class EpgScheduleHelper {
  /**
   * @param {Backend} backend
   * @param {string} channelId
   * @returns {Promise<ScheduleData>}
   */
  static getCurrentScheduleOfChannel(backend: Backend, channelId: string): Promise<ScheduleData | undefined> {
    const scheduleTimes = EpgScheduleHelper.getTodayFromUntil();
    return EpgApiV5.channelLineup(backend, channelId, scheduleTimes.from, scheduleTimes.until).then(result => {
      const now = Date.now();
      const currentSchedule = result.schedules?.find(s => {
        const start = timeStringToMs(s.published.start);
        const end = timeStringToMs(s.published.end);
        return now >= start && now <= end;
      });
      if (result.programs && currentSchedule) {
        const program = EpgScheduleHelper.findProgram(result.programs, currentSchedule.programId);
        let season: ISeasonV5 | undefined;
        let series: ISeriesV5 | undefined;
        if (program?.seasonId && result.seasons) {
          season = EpgScheduleHelper.findSeason(result.seasons, program.seasonId);
        }
        if (season?.seriesId && result.series) {
          series = EpgScheduleHelper.findSeries(result.series, season.seriesId);
        }

        if (program) {
          Log.info('Current schedule found', currentSchedule);
          return {
            schedule: currentSchedule,
            program: program,
            season: season,
            series: series,
          };
        }
      }
      return undefined;
    });
  }

  /**
   * @returns {ScheduleTimes}
   */
  static getTodayFromUntil(): ScheduleTimes {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return {
      from: today,
      until: tomorrow,
    };
  }

  static findProgram(programs: IProgramV5[], programId: string): IProgramV5 | undefined {
    return programs.find(p => p.programId === programId);
  }

  static findSeason(seasons: ISeasonV5[], seasonId: string): ISeasonV5 | undefined {
    return seasons.find(s => s.seasonId === seasonId);
  }

  static findSeries(series: ISeriesV5[], seriesId: string): ISeriesV5 | undefined {
    return series.find(s => s.seriesId === seriesId);
  }

  /**
   * @param {Backend} backend
   * @param {string} channelId
   * @returns {Promise<ScheduleData>}
   */
  static getReplaySchedule(backend: Backend, channelId: string): Promise<ScheduleData | undefined> {
    const scheduleTimes = EpgScheduleHelper.getTodayFromUntil();
    return EpgApiV5.channelLineup(backend, channelId, scheduleTimes.from, scheduleTimes.until).then(result => {
      const now = Date.now();
      const replaySchedules = result.schedules?.filter(s => {
        const end = timeStringToMs(s.published.end);
        return now > end;
      });
      if (replaySchedules) {
        const selectedSchedule = replaySchedules[1];
        if (result.programs && selectedSchedule) {
          const program = EpgScheduleHelper.findProgram(result.programs, selectedSchedule.programId);

          if (program) {
            const res: ScheduleData = { schedule: selectedSchedule, program: program };
            return res;
          }
        }
      }
      return undefined;
    });
  }
}
